import {Box, Button, Grid, Typography, useTheme} from "@mui/material";
import PermissionsPreview from "../PermissionsPreview";
import {useSalesRegions} from "../../../../hooks/useSalesRegions";

// Step 5 — Review & Confirm. Read-only summary of everything entered, grouped by step, each section
// with an Edit link back to that step. The final Create User action lives in the wizard footer.
const Step5Review = (ctx) => {
    const {draft, userTypeDisplayName, selectedGroupObjs, categories, cfg, goToStep, currentUser} = ctx;
    const theme = useTheme();

    // Sales Region is stored as an id on the draft; resolve its name for display. Called unconditionally
    // per the Rules of Hooks — the query itself is gated by `enabled`.
    const businessUnitCode = ctx.businessUnitCode ?? draft.businessUnitCode;
    const salesRegions = useSalesRegions(businessUnitCode, currentUser?.id, cfg.hasSalesRegion && !!businessUnitCode);
    const salesRegionName = salesRegions.find((r) => r.id === draft.salesRegionId)?.name ?? "";

    const section = (title, step, body) => (
        <Box sx={{border: `1px solid ${theme.palette.divider}`, borderRadius: "8px", mb: 2, overflow: "hidden"}}>
            <Box sx={{display: "flex", alignItems: "center", px: 2, py: 1.25, borderBottom: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.background.secondary}}>
                <Typography sx={{fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em"}}>{title}</Typography>
                {step != null && <Button size="small" sx={{ml: "auto", fontWeight: 700}} onClick={() => goToStep(step)}>Edit</Button>}
            </Box>
            <Box sx={{p: 2}}>{body}</Box>
        </Box>
    );

    const field = (label, value) => (
        <Grid item xs={12} sm={6}>
            <Typography sx={{fontSize: "0.625rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary"}}>{label}</Typography>
            <Typography sx={{fontSize: "0.9rem"}}>{value || <span style={{color: theme.palette.text.disabled}}>—</span>}</Typography>
        </Grid>
    );

    return (
        <Box sx={{maxWidth: 820, mx: "auto"}}>
            {section("Login", 1, (
                <Grid container spacing={2}>{field("Login", draft.login)}</Grid>
            ))}

            {section("Profile & Classification", 2, (
                <Grid container spacing={2}>
                    {field("First Name", draft.firstName)}
                    {field("Last Name", draft.lastName)}
                    {field("Country", draft.countryCode)}
                    {field("Phone Number", draft.phoneNumber)}
                    {field("Business Unit", draft.businessUnitCode)}
                    {field("User Type", userTypeDisplayName)}
                </Grid>
            ))}

            {section("Groups & Permissions", 3, (
                <>
                    <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5, mb: 2}}>
                        {selectedGroupObjs.map((g) => (
                            <Box key={g.id} sx={{display: "flex", alignItems: "center", gap: 0.5, px: 1, py: 0.4, borderRadius: "999px", border: `1px solid ${g.color}`}}>
                                <Box sx={{width: 8, height: 8, borderRadius: "50%", backgroundColor: g.color}} />
                                <Typography sx={{fontSize: "0.8rem"}}>{g.name}</Typography>
                            </Box>
                        ))}
                    </Box>
                    <PermissionsPreview selectedGroups={selectedGroupObjs} categories={categories} />
                </>
            ))}

            {hasStep4(cfg) && section("Additional Details", 4, (
                <Grid container spacing={2}>
                    {cfg.orderPin && field("Order Pin", draft.orderPin)}
                    {cfg.billto && field("Bill-to", draft.billtoId)}
                    {cfg.retailLoc && field("Retail Location", draft.retailLocationId)}
                    {cfg.shipto && field("Ship-to", draft.shiptoId)}
                    {cfg.adminAccess && field("Admin Portal Access", ctx.grantsAdmin || draft.adminPortalAccess ? "Yes" : "No")}
                    {cfg.hasSalesRegion && field("Sales Region", salesRegionName)}
                    {cfg.hasSalesTerritories && field("Sales Territories", (draft.salesTerritories || []).map((t) => t.label || t.name || "").join(", "))}
                    {cfg.hasFacilities && field("Facilities", (draft.facilities || []).map((f) => f.name).join(", "))}
                </Grid>
            ))}
        </Box>
    );
};

const hasStep4 = (cfg) =>
    !!(cfg.orderPin || cfg.shipto || cfg.billto || cfg.retailLoc || cfg.adminAccess || cfg.hasSalesRegion || cfg.hasSalesTerritories || cfg.hasFacilities);

export default Step5Review;
